/**
 * Damage Number Renderer - displays floating damage text
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events/EventBus';
import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { Camera } from '../Camera';

interface DamageNumber {
  x: number;
  y: number;
  worldX: number;
  worldY: number;
  worldZ: number;
  value: number;
  color: string;
  fontSize: number;
  age: number;
  maxAge: number;
  velocity: number;
}

export class DamageNumberRenderer {
  private numbers: DamageNumber[] = [];
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private camera: Camera | null = null;
  private world: World | null = null;
  private tempVector = new THREE.Vector3();

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'damage-numbers';
    this.canvas.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 50;
    `;
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.ctx = this.canvas.getContext('2d')!;

    document.body.appendChild(this.canvas);

    // Listen for attack events
    EventBus.on('unit:attack', (data) => {
      if (this.world) {
        const targetPos = this.world.getComponent<PositionComponent>(
          data.targetId,
          ComponentTypes.Position
        );
        if (targetPos) {
          this.spawn(targetPos.x, targetPos.y, targetPos.z, data.damage, false);
        }
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    });
  }

  setCamera(camera: Camera): void {
    this.camera = camera;
  }

  setWorld(world: World): void {
    this.world = world;
  }

  spawn(worldX: number, worldY: number, worldZ: number, damage: number, isCrit: boolean): void {
    const screenPos = this.worldToScreen(worldX, worldY + 1, worldZ);
    if (!screenPos) return;

    this.numbers.push({
      x: screenPos.x,
      y: screenPos.y,
      worldX,
      worldY: worldY + 1,
      worldZ,
      value: Math.round(damage),
      color: isCrit ? '#ff4444' : '#ffffff',
      fontSize: isCrit ? 20 : 14,
      age: 0,
      maxAge: 1.0,
      velocity: -80 - Math.random() * 20,
    });
  }

  private worldToScreen(x: number, y: number, z: number): { x: number; y: number } | null {
    if (!this.camera) return null;

    const threeCamera = this.camera.camera;
    this.tempVector.set(x, y, z);
    this.tempVector.project(threeCamera);

    // Convert to screen coordinates
    const screenX = (this.tempVector.x * 0.5 + 0.5) * this.canvas.width;
    const screenY = (-this.tempVector.y * 0.5 + 0.5) * this.canvas.height;

    // Check if in front of camera
    if (this.tempVector.z > 1) return null;

    return { x: screenX, y: screenY };
  }

  update(deltaTime: number): void {
    // Update and remove expired numbers
    for (let i = this.numbers.length - 1; i >= 0; i--) {
      const num = this.numbers[i];
      num.age += deltaTime;
      num.worldY += num.velocity * deltaTime * 0.01;
      num.velocity += 200 * deltaTime; // Gravity

      if (num.age >= num.maxAge) {
        this.numbers.splice(i, 1);
      } else {
        // Update screen position
        const screenPos = this.worldToScreen(num.worldX, num.worldY, num.worldZ);
        if (screenPos) {
          num.x = screenPos.x;
          num.y = screenPos.y;
        }
      }
    }

    this.render();
  }

  private render(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    for (const num of this.numbers) {
      const alpha = 1 - num.age / num.maxAge;
      const scale = 1 + num.age * 0.5;

      this.ctx.save();
      this.ctx.globalAlpha = alpha;
      this.ctx.font = `bold ${num.fontSize * scale}px Arial`;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';

      // Shadow
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.ctx.fillText(String(num.value), num.x + 2, num.y + 2);

      // Text
      this.ctx.fillStyle = num.color;
      this.ctx.fillText(String(num.value), num.x, num.y);

      this.ctx.restore();
    }
  }

  dispose(): void {
    this.canvas.remove();
  }
}
